import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getMeasurementUnitSymbol } from 'helpers/unitHelper';
import { condensationRiskThresholds, onlineHelpLinks, unitSymbols } from 'helpers/constants';
import { getEnvironmentStageSelector } from 'helpers/selectors';
import { TrendChart } from '../../../CommonCharts/Trend';
import { DRIVE_DC_VOLTAGE_NAME, THRESHOLD_DC_LINK_CAPACITOR } from '../../../CommonCharts/Trend/constants';
import {
    EnableUniqueScaling,
    //LastSyncDate,
    PopOutButton,
    ResetZoomButton,
    withLeftButtonGroup,
    withPopOutRow,
    withRightButtonGroup
} from 'sharedComponents/Chart/headers';
import IconCondition from 'svgIcons/MotionPortalIcons/IconCondition';
import './style.scss';
import { translate } from 'helpers/translateHelper';
import { InfoButtonWithOnlineHelp } from 'sharedComponents/InfoButtonWithOnlineHelp';

const DcVoltageInfoTextElement = ({ environment, languageCode }) => {
    return <div className='dc-voltage-info'>
        <h4 className='info-title'>
            {translate('ABB.Powertrain.Frontend.dcVoltageLabel')}
        </h4>
        <p className='info-description'>
            {translate('ABB.Powertrain.Frontend.conditionInsightsHourlyInfo')}
        </p>
        <p className='info-description'>
            <strong>{translate('ABB.Powertrain.Frontend.conditionInsightsZoomLessThan48HoursInfo')}</strong>
        </p>
        <p className='info-footer'>
            <a className='info-tooltip__link' rel='noreferrer' target='_blank' href={`${onlineHelpLinks[environment]}${languageCode}`}>
                {translate('ABB.Powertrain.Frontend.assetOverviewTooltipLinkText')}
            </a>
        </p>
    </div>;
};

const DcLinkCapacitorInfoTextElement = () => {
    return <div id='dc-link-capacitor-tooltip' className='dc-link-capacitor-tooltip'>
        <div id='dc-link-capacitor-tooltip-indicator' className='dc-link-capacitor-tooltip__container'>
            <h1 className='dc-link-capacitor-tooltip__title'>{translate('ABB.Powertrain.Frontend.dcLinkCapacitorInfoTitle')}</h1>
            <span className='dc-link-capacitor-tooltip__section'>{translate('ABB.Powertrain.Frontend.dcLinkCapacitorInfoIntro')}</span>
        </div>
        <div id='dc-link-capacitor-tooltip-how-works' className='dc-link-capacitor-tooltip__container'>
            <h1 className='dc-link-capacitor-tooltip__title'>{translate('ABB.Powertrain.Frontend.dcLinkCapacitorInfoHowWorksTitle')}</h1>
            <span className='dc-link-capacitor-tooltip__section'>{translate('ABB.Powertrain.Frontend.dcLinkCapacitorInfoHowWorksBody')}</span>
        </div>
        <div id='dc-link-capacitor-tooltip-how-to-read' className='dc-link-capacitor-tooltip__container'>
            <h1 className='dc-link-capacitor-tooltip__title'>{translate('ABB.Powertrain.Frontend.dcLinkCapacitorInfoHowToReadTitle')}</h1>
            <ul className='dc-link-capacitor-tooltip__list'>
                <li>{translate('ABB.Powertrain.Frontend.dcLinkCapacitorInfoGood')}</li>
                <li>{translate('ABB.Powertrain.Frontend.dcLinkCapacitorInfoAlert')}</li>
                <li>{translate('ABB.Powertrain.Frontend.dcLinkCapacitorInfoAlarm')}</li>
            </ul>
        </div>
    </div>;
};

const TrendChartComponent = ({
    model: {
        selectedTrendKPIs,
        userLimits,
        enableUniqueScaling,
        lastMeasurementTimeStampLabel,
        availableTrendKPIs,
        selectedTrendsData: {
            data,
            loadingStatus,
            zoom: { isZoomed },
            chartID
        },
        assetOverAllStatus,
        title,
        warningLevels,
        warningLines,
        conditionTrendIndicators,
        highErrorValue,
        highAlertLimitValue,
        lowErrorValue,
        lowAlertValue,
        unitGroupId,
        valueType,
        conditionTrendsData,
        conditionForecast,
        sharedXAxisMax,
        sharedForecastSeparatorX,
    },
    assetId,
    actions: {
        reloadAllSelectedTrendKpiData,
        setIsZoomed,
        zoomInToSelectedTrendKpiData,
        removeSelectedKpiLegend
    },
    assetDetails,
    chartRef
}) => {

    const { i18n } = useTranslation();
    const environment = useSelector(getEnvironmentStageSelector);
    const languageCode = i18n.language;

    const isCondensationRisk = Array.isArray(selectedTrendKPIs) && selectedTrendKPIs.includes('Threshold_CondensationRisk');
    const isDcVoltage = title?.toLowerCase().includes(DRIVE_DC_VOLTAGE_NAME);
    const isDcLinkCapacitor = Array.isArray(selectedTrendKPIs) && selectedTrendKPIs.includes(THRESHOLD_DC_LINK_CAPACITOR);
    const convertedUnit = getMeasurementUnitSymbol(unitGroupId);
    const isDeltaFahrenheit = convertedUnit === unitSymbols.DeltaFahrenheit;
    const condensationRiskThresholdsByUnit = isDeltaFahrenheit ?
        condensationRiskThresholds.deltaFahrenheit :
        condensationRiskThresholds.metric;

    const zoomInServer = (assetId, from, to) => {
        zoomInToSelectedTrendKpiData(assetId, from, to);
    };

    const resetServerZoom = (assetId) => {
        setIsZoomed(false);
        reloadAllSelectedTrendKpiData(assetId);
    };

    const InfoTextElement = () => {
        return <div id='condensation-risk-tooltip' className='condensation-risk-tooltip'>
            <div id='condensation-risk-tooltip-indicator' className='condensation-risk-tooltip__container'>
                <h1 className='condensation-risk-tooltip__title'>{translate('ABB.Powertrain.Frontend.condensationRiskIndicatorTitle')}</h1>
                <span className='condensation-risk-tooltip__section'>{translate('ABB.Powertrain.Frontend.condensationRiskInfoIntro')}</span>
            </div>

            <div id='condensation-risk-tooltip-how-works' className='condensation-risk-tooltip__container'>
                <h1 className='condensation-risk-tooltip__title'>{translate('ABB.Powertrain.Frontend.condensationRiskInfoHowWorksTitle')}</h1>
                <span className='condensation-risk-tooltip__section'>{translate('ABB.Powertrain.Frontend.condensationRiskInfoHowWorksBody')}</span>
            </div>

            <div id='condensation-risk-tooltip-how-to-read' className='condensation-risk-tooltip__container'>
                <h1 className='condensation-risk-tooltip__title'>{translate('ABB.Powertrain.Frontend.condensationRiskInfoHowToReadTitle')}</h1>
                <span className='condensation-risk-tooltip__section'>{translate('ABB.Powertrain.Frontend.condensationRiskInfoHowToReadBody')}</span>
                <ul className='condensation-risk-tooltip__list'>
                    {[
                        'ABB.Powertrain.Frontend.condensationRiskInfoGood',
                        'ABB.Powertrain.Frontend.condensationRiskInfoAlert',
                        'ABB.Powertrain.Frontend.condensationRiskInfoAlarm',
                        'ABB.Powertrain.Frontend.condensationRiskInfoDewPoint'
                    ].map(key => <li key={key}>{translate(key, {
                        unit: convertedUnit,
                        goodThreshold: condensationRiskThresholdsByUnit.good,
                        alertMin: condensationRiskThresholdsByUnit.alertMin,
                        alertMax: condensationRiskThresholdsByUnit.alertMax,
                        alarmThreshold: condensationRiskThresholdsByUnit.alarm,
                        dewPointThreshold: condensationRiskThresholdsByUnit.dewPoint
                    })}</li>)}
                </ul>
            </div>
        </div>;
    };

    const AssetStatusIndicator = () => {
        return <div className='kpi_status_title'>
            <span><IconCondition condition={assetOverAllStatus}></IconCondition></span>
            <b>{title}</b>
            {isCondensationRisk && <InfoButtonWithOnlineHelp infoText={InfoTextElement()} withoutLink />}
            {isDcVoltage && <InfoButtonWithOnlineHelp infoText={<DcVoltageInfoTextElement environment={environment} languageCode={languageCode} />} withoutLink />}
            {isDcLinkCapacitor && <InfoButtonWithOnlineHelp infoText={<DcLinkCapacitorInfoTextElement />} withoutLink />}
        </div>;
    };

    const CustomHeader = useMemo(() => withPopOutRow([
        withLeftButtonGroup([AssetStatusIndicator, EnableUniqueScaling]),
        withRightButtonGroup([ResetZoomButton, PopOutButton])]),
        [assetOverAllStatus, title, isCondensationRisk, isDcVoltage, isDcLinkCapacitor, environment, languageCode, convertedUnit]);
    return (
        <TrendChart
            key={chartID}
            selectedTrendKPIs={selectedTrendKPIs}
            selectedTrendsData={data}
            removeSelectedKpiLegend={removeSelectedKpiLegend}
            trendDataLoadingStatus={loadingStatus}
            userLimits={userLimits}
            enableUniqueScaling={enableUniqueScaling}
            lastMeasurementTimeStampLabel={lastMeasurementTimeStampLabel}
            LastSyncDate={false}
            isZoomed={isZoomed}
            availableTrendKPIs={availableTrendKPIs}
            chartKey={chartID}
            reloadAllSelectedTrendKpiData={resetServerZoom}
            zoomInToSelectedTrendKpiData={zoomInServer}
            setIsZoomed={setIsZoomed}
            mainChartHeader={CustomHeader}
            yAxisTitleVisible={isCondensationRisk || isDcLinkCapacitor}
            warningLevels={warningLevels}
            warningLines={warningLines}
            conditionTrendIndicators={conditionTrendIndicators}
            highErrorValue={highErrorValue}
            highAlertLimitValue={highAlertLimitValue}
            lowErrorValue={lowErrorValue}
            lowAlertValue={lowAlertValue}
            unitGroupId={unitGroupId}
            title={title}
            assetId={assetId}
            assetDetails={assetDetails}
            valueType={valueType}
            conditionTrendsData={conditionTrendsData}
            conditionForecast={conditionForecast}
            sharedXAxisMax={sharedXAxisMax}
            sharedForecastSeparatorX={sharedForecastSeparatorX}
            chartRef={chartRef}
        />
    );
};

DcVoltageInfoTextElement.propTypes = {
    environment: PropTypes.string,
    languageCode: PropTypes.string
};

TrendChartComponent.propTypes = {
    actions: PropTypes.shape({
        reloadAllSelectedTrendKpiData: PropTypes.func,
        setIsZoomed: PropTypes.func,
        zoomInToSelectedTrendKpiData: PropTypes.func,
        removeSelectedKpiLegend: PropTypes.func
    }),
    model: PropTypes.shape({
        selectedTrendKPIs: PropTypes.array,
        enableUniqueScaling: PropTypes.bool,
        lastMeasurementTimeStampLabel: PropTypes.string,
        selectedTrendsData: PropTypes.shape({
            loadingStatus: PropTypes.string,
            data: PropTypes.array,
            zoom: PropTypes.shape({
                isZoomed: PropTypes.bool,
            }),
            chartID: PropTypes.string
        }),
        userLimits: PropTypes.array,
        assetOverAllStatus: PropTypes.number,
        title: PropTypes.string,
        warningLevels: PropTypes.array,
        warningLines: PropTypes.array,
        conditionTrendIndicators: PropTypes.array,
        highErrorValue: PropTypes.number,
        highAlertLimitValue: PropTypes.number,
        lowErrorValue: PropTypes.number,
        lowAlertValue: PropTypes.number,
        unitGroupId: PropTypes.number,
        valueType: PropTypes.string,
        availableTrendKPIs: PropTypes.array,
        conditionTrendsData: PropTypes.array,
        conditionForecast: PropTypes.array,
        sharedXAxisMax: PropTypes.number,
        sharedForecastSeparatorX: PropTypes.number,

    }),
    assetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    assetDetails: PropTypes.object,
    chartRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.any })
    ]),
};

export default React.memo(TrendChartComponent);
